export function formatCurrency(fils: number): string {
  return `JD ${(fils / 1000).toFixed(3)}`
}

// SQLite's `datetime('now')` returns "YYYY-MM-DD HH:MM:SS" with no timezone
// marker. JS Date parses those as LOCAL time, but they're actually UTC —
// which is why every timestamp drifts by the user's offset. Force-parse as
// UTC, then let toLocaleString render in the browser's resolved timezone so
// a Jordan admin sees Jordan time and a US admin sees US time.
function parseUtc(iso: string): Date {
  if (
    /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(iso) &&
    !/[+-]\d{2}:?\d{2}$|Z$/.test(iso)
  ) {
    return new Date(iso.replace(' ', 'T') + 'Z')
  }
  return new Date(iso)
}

export function formatDate(iso: string): string {
  return parseUtc(iso).toLocaleDateString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
  })
}

export function formatDateTime(iso: string): string {
  return parseUtc(iso).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

/// Compact label for a scheduled-delivery window — e.g. "Mon 7–8pm" or
/// "Today 19–20" depending on locale. Uses a 24h time format for
/// consistency with the driver app + dispatch UX.
export function formatScheduledWindow(iso: string, windowMinutes: number): string {
  const start = parseUtc(iso)
  if (Number.isNaN(start.getTime())) return ''
  const end = new Date(start.getTime() + windowMinutes * 60_000)
  const today = new Date()
  const isToday = start.toDateString() === today.toDateString()
  const tomorrow = new Date(today.getTime() + 86_400_000)
  const isTomorrow = start.toDateString() === tomorrow.toDateString()
  const datePart = isToday
    ? 'Today'
    : isTomorrow
      ? 'Tomorrow'
      : start.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short' })
  const fmt = (d: Date) =>
    `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`
  return `${datePart} ${fmt(start)}–${fmt(end)}`
}

export const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  // Derived status — backed by the returned_to_store_at marker column
  // on orders. See migration 0082 + the marker-vs-enum note in
  // backend/worker/src/routes/admin/orders.ts.
  returned_to_store: 'Returned to store',
}

export const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-500/15 text-yellow-400',
  confirmed: 'bg-blue-500/15 text-blue-400',
  preparing: 'bg-purple-500/15 text-purple-400',
  out_for_delivery: 'bg-orange-500/15 text-orange-400',
  delivered: 'bg-green-500/15 text-green-400',
  cancelled: 'bg-red-500/15 text-red-400',
  // Amber to distinguish from cancelled — a return is an operational
  // event with inventory recovery, not just a customer cancellation.
  returned_to_store: 'bg-amber-500/15 text-amber-400',
}

// Derive the effective status of an order from its row. Orders with a
// returned_to_store_at timestamp are surfaced as 'returned_to_store'
// even though their `status` column is 'cancelled' — see migration 0082
// for why this is a marker column rather than a new enum value.
export function effectiveOrderStatus(o: { status: string; returned_to_store_at?: string | null }): string {
  if (o.status === 'cancelled' && o.returned_to_store_at) return 'returned_to_store'
  return o.status
}

export const ROLE_LABELS: Record<string, string> = {
  super_admin: 'Super Admin',
  operations: 'Operations',
  accounting: 'Accounting',
  marketing: 'Marketing',
}
